import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  FolderTree,
  Plus,
  Edit3,
  Trash2,
  Eye,
  Layers,
  ListChecks,
  ChevronDown,
  ChevronUp,
  Copy,
  Download,
  Upload,
  FileSpreadsheet,
  Check,
  X,
  Tag,
  Palette,
  Lock,
  Blocks
} from 'lucide-react';
import logger from '@/lib/logger';
import PhaseBlocksTab from '@/components/templates/PhaseBlocksTab';

function ProjectTemplates({ user }) {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Template modal
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [viewingTemplate, setViewingTemplate] = useState(null);
  
  // Create from project modal
  const [fromProjectModalOpen, setFromProjectModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  
  // Project Type modal
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm, setTypeForm] = useState({ name: '', color: '#166534' });
  
  // Expanded phases for view
  const [expandedPhases, setExpandedPhases] = useState({});
  
  // Form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    project_type: '',
    description: '',
    phases: []
  });
  
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get('/project-templates');
      // Sort templates alphabetically by name
      const sortedTemplates = response.data.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      );
      setTemplates(sortedTemplates);
    } catch (error) {
      logger.error('Error fetching templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      logger.error('Error fetching projects:', error);
    }
  }, []);

  const fetchProjectTypes = useCallback(async () => {
    try {
      const response = await api.get('/project-types');
      // Sort project types alphabetically by name
      const sortedTypes = response.data.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      );
      setProjectTypes(sortedTypes);
    } catch (error) {
      logger.error('Error fetching project types:', error);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
    fetchProjects();
    fetchProjectTypes();
  }, [fetchTemplates, fetchProjects, fetchProjectTypes]);
  
  // Phase management
  const addPhase = () => {
    const newPhase = {
      _uid: `phase-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: `Fase ${templateForm.phases.length + 1}`,
      weight: 25,
      order: templateForm.phases.length,
      tasks: []
    };
    setTemplateForm(prev => ({
      ...prev,
      phases: [...prev.phases, newPhase]
    }));
  };
  
  const removePhase = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index).map((p, i) => ({ ...p, order: i }))
    }));
  };
  
  const updatePhase = (index, field, value) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[index] = { ...newPhases[index], [field]: value };
      return { ...prev, phases: newPhases };
    });
  };
  
  // Task management
  const addTask = (phaseIndex) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex].tasks = [
        ...(newPhases[phaseIndex].tasks || []),
        { _uid: `task-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: '', weight: 10 }
      ];
      return { ...prev, phases: newPhases };
    });
  };
  
  const updateTask = (phaseIndex, taskIndex, field, value) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex].tasks[taskIndex] = { 
        ...newPhases[phaseIndex].tasks[taskIndex], 
        [field]: value 
      };
      return { ...prev, phases: newPhases };
    });
  };
  
  const removeTask = (phaseIndex, taskIndex) => {
    setTemplateForm(prev => {
      const newPhases = [...prev.phases];
      newPhases[phaseIndex].tasks = newPhases[phaseIndex].tasks.filter((_, i) => i !== taskIndex);
      return { ...prev, phases: newPhases };
    });
  };
  
  // Stable UID generator for phase/task array keys (avoids index-as-key)
  const uid = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Modal handlers
  const openNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      name: '',
      project_type: '',
      description: '',
      phases: [{
        _uid: uid('phase'),
        name: 'Fase 1', weight: 100, order: 0,
        tasks: [{ _uid: uid('task'), name: '', weight: 100 }]
      }]
    });
    setTemplateModalOpen(true);
  };
  
  const openEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      project_type: template.project_type,
      description: template.description || '',
      phases: template.phases.map((p, idx) => ({
        _uid: uid('phase'),
        name: p.name,
        weight: p.weight,
        order: idx,
        tasks: (p.tasks || []).map(t => ({ _uid: uid('task'), name: t.name, weight: t.weight }))
      }))
    });
    setTemplateModalOpen(true);
  };
  
  const openViewTemplate = (template) => {
    setViewingTemplate(template);
    setExpandedPhases({});
    setViewModalOpen(true);
  };
  
  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    
    if (!templateForm.name || !templateForm.project_type) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    try {
      const payload = {
        name: templateForm.name,
        project_type: templateForm.project_type,
        description: templateForm.description,
        phases: templateForm.phases.map((p, idx) => ({
          name: p.name,
          weight: p.weight,
          order: idx,
          tasks: p.tasks.filter(t => t.name).map(t => ({ name: t.name, weight: t.weight }))
        }))
      };
      
      if (editingTemplate) {
        await api.put(`/project-templates/${editingTemplate.template_id}`, payload);
        toast.success('Template atualizado!');
      } else {
        await api.post('/project-templates', payload);
        toast.success('Template criado!');
      }
      
      setTemplateModalOpen(false);
      fetchTemplates();
    } catch (error) {
      logger.error('Error saving template:', error);
      toast.error('Erro ao salvar template');
    }
  };
  
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Deseja excluir este template?')) return;
    try {
      await api.delete(`/project-templates/${templateId}`);
      toast.success('Template removido!');
      fetchTemplates();
    } catch (error) {
      toast.error('Erro ao excluir template');
    }
  };
  
  const handleCreateFromProject = async () => {
    if (!selectedProject || !newTemplateName) {
      toast.error('Selecione um projeto e defina um nome');
      return;
    }
    
    try {
      await api.post(`/project-templates/from-project/${selectedProject}?name=${encodeURIComponent(newTemplateName)}`);
      toast.success('Template criado a partir do projeto!');
      setFromProjectModalOpen(false);
      setSelectedProject(null);
      setNewTemplateName('');
      fetchTemplates();
    } catch (error) {
      logger.error('Error creating template:', error);
      toast.error('Erro ao criar template');
    }
  };
  
  // ========== PROJECT TYPE HANDLERS ==========
  
  const openNewType = () => {
    setEditingType(null);
    setTypeForm({ name: '', color: '#166534' });
    setTypeModalOpen(true);
  };
  
  const openEditType = (type) => {
    setEditingType(type);
    setTypeForm({ name: type.name, color: type.color || '#166534' });
    setTypeModalOpen(true);
  };
  
  const handleSaveType = async (e) => {
    e.preventDefault();
    
    if (!typeForm.name.trim()) {
      toast.error('Digite o nome do tipo');
      return;
    }
    
    try {
      if (editingType) {
        await api.put(`/project-types/${editingType.type_id}`, typeForm);
        toast.success('Tipo de projeto atualizado!');
      } else {
        await api.post('/project-types', typeForm);
        toast.success('Tipo de projeto criado!');
      }
      setTypeModalOpen(false);
      setEditingType(null);
      fetchProjectTypes();
    } catch (error) {
      logger.error('Error saving type:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar tipo de projeto');
    }
  };
  
  const handleDeleteType = async (type) => {
    if (type.is_default) {
      toast.error('Não é possível excluir tipos padrão');
      return;
    }
    
    if (!window.confirm(`Deseja excluir o tipo "${type.name}"? Projetos existentes com este tipo não serão afetados.`)) return;
    
    try {
      await api.delete(`/project-types/${type.type_id}`);
      toast.success('Tipo de projeto removido!');
      fetchProjectTypes();
    } catch (error) {
      logger.error('Error deleting type:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir tipo');
    }
  };
  
  const togglePhaseExpand = (phaseIdx) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseIdx]: !prev[phaseIdx]
    }));
  };
  
  const getTotalTasks = (template) => {
    return template.phases?.reduce((sum, p) => sum + (p.tasks?.length || 0), 0) || 0;
  };
  
  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Templates de Projeto</h1>
            <p className="text-gray-600 mt-1">Gerencie templates com fases e tarefas padrão por tipologia</p>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <FolderTree className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
                <p className="text-xs text-gray-500">Templates</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border border-blue-200 bg-blue-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Layers className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {templates.reduce((sum, t) => sum + (t.phases?.length || 0), 0)}
                </p>
                <p className="text-xs text-blue-600">Total Fases</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border border-purple-200 bg-purple-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ListChecks className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">
                  {templates.reduce((sum, t) => sum + getTotalTasks(t), 0)}
                </p>
                <p className="text-xs text-purple-600">Total Tarefas</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border border-orange-200 bg-orange-50">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Copy className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">
                  {[...new Set(templates.map(t => t.project_type))].length}
                </p>
                <p className="text-xs text-orange-600">Tipologias</p>
              </div>
            </div>
          </Card>
        </div>
        
        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="templates" data-testid="templates-tab">
              <FolderTree className="w-4 h-4 mr-2" />
              Templates
            </TabsTrigger>
            <TabsTrigger value="phase-blocks" data-testid="phase-blocks-tab">
              <Blocks className="w-4 h-4 mr-2" />
              Blocos de Fase
            </TabsTrigger>
            <TabsTrigger value="types" data-testid="types-tab">
              <Tag className="w-4 h-4 mr-2" />
              Tipos de Projeto
            </TabsTrigger>
            <TabsTrigger value="import" data-testid="import-tab">
              <Upload className="w-4 h-4 mr-2" />
              Importar Projetos
            </TabsTrigger>
          </TabsList>
          
          {/* Templates Tab */}
          <TabsContent value="templates" className="mt-4 space-y-4">
            <Card className="p-4 border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Templates de Projeto</h3>
                  <p className="text-sm text-gray-500">Defina fases e tarefas padrão para cada tipologia</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setFromProjectModalOpen(true)} variant="outline" className="border-emerald-300 text-emerald-700">
                    <Copy className="w-4 h-4 mr-2" />
                    Criar de Projeto Existente
                  </Button>
                  <Button onClick={openNewTemplate} className="bg-emerald-800 hover:bg-emerald-900" data-testid="new-template-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Template
                  </Button>
                </div>
              </div>
            </Card>
            
            {/* Templates Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <Card key={template.template_id} className="p-4 border border-gray-200 hover:border-emerald-300 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-emerald-100 rounded-lg">
                        <FolderTree className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 line-clamp-1">{template.name}</h4>
                        <Badge variant="outline" className="text-xs mt-1">{template.project_type}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openViewTemplate(template)} title="Visualizar">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditTemplate(template)} title="Editar">
                        <Edit3 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.template_id)} className="text-red-600" title="Excluir">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {template.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{template.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 pt-3 border-t border-gray-100">
                    <span className="flex items-center gap-1">
                      <Layers className="w-4 h-4" />
                      {template.phases?.length || 0} fases
                    </span>
                    <span className="flex items-center gap-1">
                      <ListChecks className="w-4 h-4" />
                      {getTotalTasks(template)} tarefas
                    </span>
                  </div>
                </Card>
              ))}
              
              {templates.length === 0 && (
                <Card className="col-span-full p-8 text-center text-gray-500">
                  <FolderTree className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum template cadastrado</p>
                  <p className="text-sm mt-1">Crie templates para agilizar a criação de projetos</p>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Phase Blocks Tab */}
          <TabsContent value="phase-blocks" className="mt-4 space-y-4">
            <PhaseBlocksTab />
          </TabsContent>

          {/* Types Tab */}
          <TabsContent value="types" className="mt-4 space-y-4">
            <Card className="p-4 border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Tipos de Projeto</h3>
                  <p className="text-sm text-gray-500">Gerencie as tipologias de projeto disponíveis no sistema</p>
                </div>
                <Button onClick={openNewType} className="bg-emerald-800 hover:bg-emerald-900" data-testid="new-type-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Tipo
                </Button>
              </div>
            </Card>
            
            {/* Types Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projectTypes.map((type) => (
                <Card key={type.type_id} className="p-4 border border-gray-200 hover:border-emerald-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: type.color + '20' }}
                      >
                        <Tag className="w-5 h-5" style={{ color: type.color }} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{type.name}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <div 
                            className="w-4 h-4 rounded-full border border-gray-200" 
                            style={{ backgroundColor: type.color }}
                            title={type.color}
                          />
                          <span className="text-xs text-gray-500">{type.color}</span>
                          {type.is_default && (
                            <Badge variant="outline" className="text-xs ml-1">
                              <Lock className="w-3 h-3 mr-1" />
                              Padrão
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {!type.is_default && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditType(type)} title="Editar">
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteType(type)} className="text-red-600" title="Excluir">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
              
              {projectTypes.length === 0 && (
                <Card className="col-span-full p-8 text-center text-gray-500">
                  <Tag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nenhum tipo de projeto cadastrado</p>
                </Card>
              )}
            </div>
            
            {/* Info Card */}
            <Card className="p-4 bg-blue-50 border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Lock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-blue-900">Tipos Padrão</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Os tipos marcados como "Padrão" são pré-configurados do sistema e não podem ser editados ou excluídos.
                    Você pode criar novos tipos personalizados para atender às suas necessidades.
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>
          
          {/* Import Tab */}
          <TabsContent value="import" className="mt-4 space-y-4">
            <Card className="p-6 border border-gray-200">
              <div className="text-center space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg inline-block">
                  <Upload className="w-12 h-12 text-gray-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">Importar Projetos em Lote</h3>
                  <p className="text-gray-500 mt-1">Faça upload de uma planilha CSV para criar múltiplos projetos</p>
                </div>
                
                <div className="max-w-md mx-auto space-y-4 pt-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-emerald-400 transition-colors cursor-pointer">
                    <input 
                      type="file" 
                      accept=".csv,.xlsx,.xls"
                      className="hidden" 
                      id="file-upload"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          toast.info(`Processando arquivo: ${file.name}`);
                          
                          try {
                            // Read file content with proper encoding
                            const buffer = await file.arrayBuffer();
                            // Try UTF-8 first, then fallback to Latin-1
                            let text;
                            try {
                              text = new TextDecoder('utf-8', { fatal: true }).decode(buffer);
                            } catch {
                              // Fallback to Latin-1 (ISO-8859-1) for Excel files
                              text = new TextDecoder('iso-8859-1').decode(buffer);
                            }
                            
                            // Remove BOM if present
                            text = text.replace(/^\uFEFF/, '');
                            
                            const lines = text.split(/\r?\n/).filter(line => line.trim());
                            
                            if (lines.length < 2) {
                              toast.error('Arquivo vazio ou sem dados');
                              return;
                            }
                            
                            // Detect separator (comma or semicolon)
                            const firstLine = lines[0];
                            const separator = firstLine.includes(';') ? ';' : ',';
                            
                            // Parse CSV headers
                            const headers = firstLine.split(separator).map(h => h.trim().toLowerCase().replace(/"/g, '').replace(/^\uFEFF/, ''));
                            
                            logger.log('Headers detected:', headers);
                            logger.log('Separator:', separator);
                            
                            // Parse data rows
                            const projects = [];
                            for (let i = 1; i < lines.length; i++) {
                              const values = lines[i].split(separator).map(v => v.trim().replace(/"/g, ''));
                              const project = {};
                              
                              headers.forEach((header, idx) => {
                                project[header] = values[idx] || '';
                              });
                              
                              logger.log(`Row ${i}:`, project);
                              
                              // Validate required fields
                              if (project.nome_projeto && project.cliente && project.tipo_projeto) {
                                projects.push(project);
                              }
                            }
                            
                            if (projects.length === 0) {
                              toast.error('Nenhum projeto válido encontrado no arquivo');
                              return;
                            }
                            
                            // Send to API
                            const response = await api.post('/projects/bulk-import', {
                              projects: projects,
                              apply_templates: true
                            });
                            
                            const result = response.data;
                            
                            if (result.success && result.success.length > 0) {
                              toast.success(`${result.success.length} projeto(s) criado(s) com sucesso!`);
                              
                              // Show details
                              result.success.forEach(s => {
                                logger.log(`✅ ${s.name}: ${s.phases_created} fases, ${s.tasks_created} tarefas`);
                              });
                            }
                            
                            if (result.errors && result.errors.length > 0) {
                              toast.error(`${result.errors.length} erro(s) durante importação`);
                              result.errors.forEach(err => {
                                logger.error(`❌ Linha ${err.row}: ${err.error}`);
                              });
                            }
                            
                            // Clear input
                            e.target.value = '';
                            
                          } catch (error) {
                            logger.error('Import error:', error);
                            toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
                          }
                        }
                      }}
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <FileSpreadsheet className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Clique para selecionar arquivo</p>
                      <p className="text-xs text-gray-400 mt-1">CSV ou XLSX (máx. 5MB)</p>
                    </label>
                  </div>
                  
                  <Button variant="outline" className="w-full" onClick={() => {
                    // Download template CSV
                    const csvContent = "nome_projeto,cliente,tipo_projeto,descricao,status,data_inicio,data_fim_prevista,consultor_responsavel\nProjeto Exemplo,Cliente ABC,Licenciamento Ambiental,Descrição do projeto,Não iniciado,2026-04-01,2026-12-31,consultor@email.com";
                    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = 'modelo_importacao_projetos.csv';
                    link.click();
                    toast.success('Modelo de planilha baixado!');
                  }}>
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Modelo de Planilha
                  </Button>
                </div>
                
                <div className="text-left max-w-lg mx-auto pt-4 border-t border-gray-200 mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Campos da Planilha:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li><strong>nome_projeto</strong> (obrigatório) - Nome do projeto</li>
                    <li><strong>cliente</strong> (obrigatório) - Nome ou email do cliente</li>
                    <li><strong>tipo_projeto</strong> (obrigatório) - Tipologia (ex: Licenciamento Ambiental)</li>
                    <li><strong>descricao</strong> - Descrição do projeto</li>
                    <li><strong>status</strong> - Status inicial (padrão: Não iniciado)</li>
                    <li><strong>data_inicio</strong> - Data de início (AAAA-MM-DD)</li>
                    <li><strong>data_fim_prevista</strong> - Data fim prevista (AAAA-MM-DD)</li>
                    <li><strong>consultor_responsavel</strong> - Email do consultor</li>
                  </ul>
                  
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm text-emerald-800">
                      <strong>Dica:</strong> Se existir um template para o tipo de projeto informado, as fases e tarefas serão criadas automaticamente!
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* View Template Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingTemplate?.name}</DialogTitle>
            <DialogDescription>
              <Badge variant="outline">{viewingTemplate?.project_type}</Badge>
            </DialogDescription>
          </DialogHeader>
          
          {viewingTemplate && (
            <div className="space-y-4 mt-4">
              {viewingTemplate.description && (
                <p className="text-sm text-gray-600">{viewingTemplate.description}</p>
              )}
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Fases e Tarefas</h4>
                {viewingTemplate.phases?.map((phase, phaseIdx) => (
                  <Card key={`view-phase-${phase.name}-${phaseIdx}`} className="border border-gray-200">
                    <div 
                      className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                      onClick={() => togglePhaseExpand(phaseIdx)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-medium text-emerald-700">
                          {phaseIdx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{phase.name}</p>
                          <p className="text-xs text-gray-500">Peso: {phase.weight}% • {phase.tasks?.length || 0} tarefas</p>
                        </div>
                      </div>
                      {expandedPhases[phaseIdx] ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                    </div>
                    
                    {expandedPhases[phaseIdx] && phase.tasks?.length > 0 && (
                      <div className="px-3 pb-3 pt-0">
                        <div className="border-t border-gray-100 pt-3">
                          <ul className="space-y-1">
                            {phase.tasks.map((task, taskIdx) => (
                              <li key={task._uid || `${phase._uid}-${taskIdx}`} className="flex items-center justify-between text-sm py-1 px-2 hover:bg-gray-50 rounded">
                                <span className="text-gray-700">• {task.name}</span>
                                <span className="text-gray-400 text-xs">Peso: {task.weight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Create/Edit Template Modal */}
      <Dialog open={templateModalOpen} onOpenChange={setTemplateModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Editar Template' : 'Novo Template de Projeto'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveTemplate} className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Nome do Template *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="Ex: Licenciamento Ambiental Padrão"
                  required
                />
              </div>
              <div>
                <Label>Tipo de Projeto *</Label>
                <Select value={templateForm.project_type} onValueChange={(v) => setTemplateForm({ ...templateForm, project_type: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTypes.map((type) => (
                      <SelectItem key={type.type_id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                placeholder="Descrição do template..."
                rows={2}
              />
            </div>
            
            {/* Phases */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-gray-900">Fases</h4>
                <Button type="button" onClick={addPhase} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Fase
                </Button>
              </div>
              
              {templateForm.phases.map((phase, phaseIdx) => (
                <Card key={phase._uid || phaseIdx} className="p-4 border border-gray-200">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex-1 grid md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <Label>Nome da Fase</Label>
                        <Input
                          value={phase.name}
                          onChange={(e) => updatePhase(phaseIdx, 'name', e.target.value)}
                          placeholder="Ex: Licença Prévia"
                        />
                      </div>
                      <div>
                        <Label>Peso (%)</Label>
                        <Input
                          type="number"
                          value={phase.weight}
                          onChange={(e) => updatePhase(phaseIdx, 'weight', parseFloat(e.target.value) || 0)}
                          step="1"
                          min="0"
                          max="100"
                        />
                      </div>
                    </div>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removePhase(phaseIdx)} className="text-red-600 mt-6">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Tasks */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Tarefas</Label>
                      <Button type="button" variant="ghost" size="sm" onClick={() => addTask(phaseIdx)}>
                        <Plus className="w-3 h-3 mr-1" />
                        Adicionar Tarefa
                      </Button>
                    </div>
                    {phase.tasks?.map((task, taskIdx) => (
                      <div key={task._uid || `${phase._uid || phaseIdx}-${taskIdx}`} className="flex items-center gap-2">
                        <Input
                          value={task.name}
                          onChange={(e) => updateTask(phaseIdx, taskIdx, 'name', e.target.value)}
                          placeholder="Nome da tarefa"
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          value={task.weight}
                          onChange={(e) => updateTask(phaseIdx, taskIdx, 'weight', parseFloat(e.target.value) || 0)}
                          placeholder="Peso"
                          className="w-20"
                          min="0"
                        />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeTask(phaseIdx, taskIdx)} className="text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setTemplateModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                {editingTemplate ? 'Salvar Alterações' : 'Criar Template'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Create from Project Modal */}
      <Dialog open={fromProjectModalOpen} onOpenChange={setFromProjectModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Criar Template de Projeto Existente</DialogTitle>
            <DialogDescription>
              Selecione um projeto para usar suas fases e tarefas como base
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Projeto Base</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um projeto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Nome do Template *</Label>
              <Input
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Ex: Licenciamento - Posto de Combustíveis"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setFromProjectModalOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateFromProject} className="bg-emerald-800 hover:bg-emerald-900">
                Criar Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Project Type Modal */}
      <Dialog open={typeModalOpen} onOpenChange={setTypeModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingType ? 'Editar Tipo de Projeto' : 'Novo Tipo de Projeto'}</DialogTitle>
            <DialogDescription>
              {editingType ? 'Altere o nome e a cor do tipo de projeto' : 'Crie um novo tipo de projeto personalizado'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSaveType} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="type-name">Nome do Tipo *</Label>
              <Input
                id="type-name"
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="Ex: Regularização Fundiária"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="type-color">Cor</Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="color"
                  id="type-color"
                  value={typeForm.color}
                  onChange={(e) => setTypeForm({ ...typeForm, color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border border-gray-200"
                />
                <Input
                  value={typeForm.color}
                  onChange={(e) => setTypeForm({ ...typeForm, color: e.target.value })}
                  placeholder="#166534"
                  className="flex-1"
                />
              </div>
              
              {/* Color Presets */}
              <div className="flex flex-wrap gap-2 mt-3">
                {['#166534', '#1E40AF', '#7C2D12', '#4C1D95', '#0F766E', '#DC2626', '#EA580C', '#CA8A04', '#65A30D', '#0891B2'].map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setTypeForm({ ...typeForm, color })}
                    className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
                    style={{ 
                      backgroundColor: color,
                      borderColor: typeForm.color === color ? '#000' : 'transparent'
                    }}
                    title={color}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setTypeModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900">
                {editingType ? 'Salvar Alterações' : 'Criar Tipo'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default ProjectTemplates;
